import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ABACPolicyEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useABACPolicyAction} from "./useABACPolicyAction";

const ALL_ABAC_POLICY_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type ABACPolicyAction = typeof ALL_ABAC_POLICY_ACTIONS[number];

export type ABACPolicyActionMenuDropdownProps = {
  abacPolicy: ABACPolicyEntity
  actions?: ABACPolicyAction[]
  onActionClick?: (action: ABACPolicyAction) => void
  showEmpty?: boolean
}

export const ABACPolicyActionMenuDropdown = (props: ABACPolicyActionMenuDropdownProps) => {
  const {
    abacPolicy,
    actions = ALL_ABAC_POLICY_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<ABACPolicyAction> = useMemo(() => {
    const enabledActions = new Set<ABACPolicyAction>()
    const actionsSet = actions ? new Set<ABACPolicyAction>(actions) : new Set<ABACPolicyAction>()

    if (hasAccess("abacpolicy_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("abacpolicy_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [abacPolicy, actions, hasAccess])

  const {
    onABACPolicyActionClick
  } = useABACPolicyAction()

  const _onActionClick = useCallback((action: ABACPolicyAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onABACPolicyActionClick(abacPolicy, action)
    }
  }, [onActionClick, onABACPolicyActionClick, abacPolicy])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
